import { TermsLink } from "./LegalNotice";

/**
 * Legal consent checkbox shown right above a form's submit button.
 * Combines Terms acceptance, communications consent, e-signature acknowledgment,
 * and (where relevant) HIPAA reminder into a single required checkbox.
 *
 * `type` tailors the wording per form: care | consultation | contact | careers | referral
 */
export default function FormConsent({ type = "care", checked, onChange, testId = "form-legal-consent" }) {
  const lines = {
    care: (
      <>
        By submitting this care request, I confirm the information provided is accurate, agree to Melo Hopeful Care's{" "}
        <TermsLink />, consent to receiving calls, texts and emails about my request, and acknowledge this submission
        serves as my electronic signature. This form is <strong>not for medical emergencies</strong> — call 911 for
        medical emergencies.
      </>
    ),
    consultation: (
      <>
        By booking this consultation, I agree to Melo Hopeful Care's <TermsLink />, consent to appointment reminders by
        phone, text and email, and understand that missed appointments or same-day cancellations may incur a fee per
        Section 13 of the Terms. This submission serves as my electronic signature.
      </>
    ),
    contact: (
      <>
        By sending this message, I agree to Melo Hopeful Care's <TermsLink /> and consent to being contacted by phone,
        text or email regarding my inquiry. Please <strong>do not include sensitive medical details</strong> in this
        form — use secure channels once a care coordinator reaches out.
      </>
    ),
    careers: (
      <>
        By submitting this application, I certify that the information and any uploaded documents are accurate and mine
        to share, agree to Melo Hopeful Care's <TermsLink />, consent to being contacted about hiring next steps, and
        acknowledge that this submission and the uploaded files constitute my electronic signature. I understand hiring
        is contingent on eligibility, references and a background check.
      </>
    ),
    referral: (
      <>
        By submitting this partnership request, I represent that I have authority to act on behalf of the organization
        named above, agree to Melo Hopeful Care's <TermsLink />, and consent to being contacted regarding this
        partnership inquiry.
      </>
    ),
  };

  return (
    <label
      htmlFor={testId}
      className="flex items-start gap-3 p-4 rounded-xl border-2 border-slate-100 bg-slate-50/60 hover:border-teal-200 transition cursor-pointer"
      data-testid={`${testId}-wrapper`}
    >
      <input
        id={testId}
        type="checkbox"
        required
        checked={checked}
        onChange={onChange}
        className="mt-1 h-4 w-4 rounded accent-teal-600 shrink-0"
        data-testid={testId}
      />
      <span className="text-[13px] text-slate-700 leading-relaxed">{lines[type] || lines.care}</span>
    </label>
  );
}
